import { useMemo } from "react";

import { type GridColumn } from "commonComponents/utilities/Grid/GridContainer.types";

/**
 * Column types whose `Cell` renders an entity link (`<a href>`). The card
 * uses the first matching column as the whole-card tap target, so we sort
 * these to the top of the cardable column list to keep the headline-and-tap
 * affordance in sync. Convention: column types that wrap a navigable entity
 * end with "Link" (e.g., `warrantyLink`, `billLink`, `purchaseOrderLink`).
 */
const LINK_COLUMN_TYPE_PATTERN = /Link$/;

const isLinkColumn = (column: GridColumn<unknown>): boolean =>
    LINK_COLUMN_TYPE_PATTERN.test(column.columnType);

/**
 * Filters `columnsListAll` down to the columns that should appear in the
 * card. A column is "cardable" iff the server has flagged it `IsAlwaysIncluded`
 * and not hidden it. Columns like "Job" are always-included but hidden in
 * single-job contexts via `ShowOnMultiJobsOnly` server-side.
 *
 * Within the result, link-typed columns are pulled to the top so the first
 * cardable column is reliably the entity link (and therefore the card's
 * headline + tap target). Relative order is otherwise preserved from the
 * source list, except any `trailingColumnIds` are pinned to the bottom (in the
 * order listed) so they read as a card footer (e.g. a total).
 */
export const getCardColumns = (
    columnsListAll: GridColumn<unknown>[],
    trailingColumnIds?: readonly string[],
): GridColumn<unknown>[] => {
    const cardable = columnsListAll.filter((col) => col.isAlwaysIncluded && !col.isHidden);
    const trailing = new Set(trailingColumnIds ?? []);
    const linkColumns = cardable.filter((col) => isLinkColumn(col) && !trailing.has(col.id));
    const otherColumns = cardable.filter((col) => !isLinkColumn(col) && !trailing.has(col.id));
    // Pinned columns render last, in the order the caller listed them (not source order).
    const trailingColumns = (trailingColumnIds ?? [])
        .map((id) => cardable.find((col) => col.id === id))
        .filter((col): col is GridColumn<unknown> => col !== undefined);
    return [...linkColumns, ...otherColumns, ...trailingColumns];
};

export const useCardColumns = (
    columnsListAll: GridColumn<unknown>[],
    trailingColumnIds?: readonly string[],
): GridColumn<unknown>[] =>
    useMemo(
        () => getCardColumns(columnsListAll, trailingColumnIds),
        [columnsListAll, trailingColumnIds],
    );
